import React, { useState } from 'react';
import TopTiktokAudiosTable, { TopTiktokAudiosTableRow } from './TopTiktokAudiosTable';
import { getTrndsClientSongTiktokAudioStats } from '@round/api';
import { StylesConfig } from 'react-select';
import { AudioPlayerProvider } from '../../../../../AudioPlayer/AudioPlayerContext';
import styles from './TopTiktokAudios.module.css';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import { numberWithCommas } from '../../../../../../helpers';
import SearchInput from '../../../../../../ui/DataEntry/SearchInput/SearchInput';
import Select from '../../../../../../ui/DataEntry/Select/Select';
import { GenericDropdownOption } from '../../../../../../App.types';
import useTopTiktokAudios from './useTopTiktokAudios';
import ClientTiktokAudioModal from '../../../../../TikTok/audios/features/details/ClientTiktokAudioModal/ClientTiktokAudioModal';
import Button from '../../../../../../ui/Buttons/Button/Button';
import LeftArrowIcon from '../../../../../../SharedComponents/svg/Icons/LeftArrowIcon';

type TopTiktokAudiosProps = {
    songId: number;
    clientId: number;
};

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

type Ordering = Parameters<typeof getTrndsClientSongTiktokAudioStats>[0]['ordering'];

const sortingOptions: GenericDropdownOption<Ordering>[] = [
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

const TopTiktokAudios = ({ songId, clientId }: TopTiktokAudiosProps) => {
    const [search, setSearch] = useState('');
    const [ordering, setOrdering] = useState<Ordering>('-video_count_daily_change');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
    const [selectedAudioRow, setSelectedAudioRow] = useState<TopTiktokAudiosTableRow | null>(null);

    const { count, reset, data, loading } = useTopTiktokAudios({
        clientId,
        songId,
        page,
        search,
        page_size: pageSize,
        ordering: ordering,
    });

    const [audiosRowsDisplayed, setAudiosRowsDisplayed] = useState(0);
    const audiosRecordsShowingLabel = `displaying ${audiosRowsDisplayed} out of ${numberWithCommas(count)} audio${
        count === 1 ? '' : 's'
    }`;

    return (
        <div>
            <div className={styles.titles}>
                <p className={styles.title}>Top Audios</p>
                <p className={styles.subtitle}>{loading ? <Skeleton width="20rem" /> : audiosRecordsShowingLabel}</p>
            </div>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <SearchInput
                        className={styles.searchInput}
                        value={search}
                        onChange={(search) => {
                            setSearch(search);
                            reset();
                        }}
                    />
                </div>
                <div className={styles.headerRight}>
                    <label className={styles.label}>Sort by</label>
                    <div data-test-id="sort-container">
                        <Select
                            isSearchable={false}
                            styles={sortingSelectStyles}
                            value={sortingOptions.find((option) => option.value === ordering)}
                            options={sortingOptions}
                            onChange={(value) => {
                                setOrdering((value ?? sortingOptions[1])?.value);
                                reset();
                            }}
                        />
                    </div>
                </div>
            </div>
            <AudioPlayerProvider>
                <TopTiktokAudiosTable
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={(pageSize) => setPageSize(pageSize ?? 10)}
                    data={data}
                    count={count}
                    loading={loading}
                    order={ordering}
                    onPaginationChange={(state) => setAudiosRowsDisplayed(state.pageRows.length)}
                    onRowClick={(row) => {
                        setIsAudioModalOpen(true);
                        setSelectedAudioRow(row);
                    }}
                />

                <ClientTiktokAudioModal
                    modalClassName={styles.audioModal}
                    isModalOpen={isAudioModalOpen}
                    closeModal={() => {
                        setIsAudioModalOpen(false);
                    }}
                    audioId={selectedAudioRow?.audio_id}
                    clientId={clientId}
                    audioStatsTableRow={selectedAudioRow}
                    renderCloseButton={(close) => (
                        <Button
                            className={styles.audioModalBackButton}
                            contentClassName={styles.audioModalBackButtonContent}
                            onClick={close}
                        >
                            <LeftArrowIcon width={10} height={10} className={styles.audioModalBackButtonIcon} /> Back
                        </Button>
                    )}
                />
            </AudioPlayerProvider>
        </div>
    );
};

export default TopTiktokAudios;
